/**
 * `/sign-in` — the two-step volunteer sign-in page (M12b).
 *
 * Step 1 accepts an email and POSTs it to `/volunteer/sign-in`. On a 200
 * we advance to step 2; on 404 `email_not_registered` we tell the
 * visitor to contact their school admin and stay on step 1.
 *
 * Step 2 accepts the 6-digit OTP and POSTs it together with the email
 * captured in step 1 to `/volunteer/sign-in/verify`. On success the API
 * sets the `isms_volunteer_session` cookie and returns the minimal
 * profile, at which point we redirect the volunteer to `/me`.
 *
 * On a failed verify we surface the PRD-mandated copy "That code didn't
 * work. It may have expired — request a new one." alongside a **Resend
 * code** affordance that re-runs step 1 with the already-known email, and
 * a **Back** affordance that returns to the email input.
 */

import { useState, useId, useRef, useEffect, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client.js';
import type { ApiClient } from '../api/client.js';
import { ErrorBanner } from '../components/ErrorBanner.js';

export interface SignInPageProps {
  /** Injected API client — defaults to the module-level `apiClient` in
   *  production, overridable in tests. */
  client: ApiClient;
  /** Where to send the volunteer after a successful verify. Defaults to
   *  `/me`. Tests override with `/me-test` to decouple from the router
   *  setup. */
  redirectTo?: string;
}

type Step = 'enter-email' | 'enter-code';

export function SignInPage({ client, redirectTo = '/me' }: SignInPageProps): JSX.Element {
  const [step, setStep] = useState<Step>('enter-email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiError | string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  const emailInputId = useId();
  const codeInputId = useId();
  const errorId = useId();
  const infoId = useId();

  const codeInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === 'enter-code') {
      codeInputRef.current?.focus();
    } else {
      emailInputRef.current?.focus();
    }
  }, [step]);

  async function requestCode(targetEmail: string, isResend: boolean): Promise<void> {
    setPending(true);
    setError(null);
    setInfoMessage(null);
    try {
      await client.volunteerSignIn({ email: targetEmail });
      setStep('enter-code');
      setInfoMessage(
        isResend
          ? 'A fresh code has been sent. Check your email.'
          : "We've sent you a 6-digit code. Check your email and enter it below.",
      );
    } catch (err) {
      if (err instanceof ApiError && err.code === 'email_not_registered') {
        setError(
          "That email isn't registered as a volunteer at this school. Please contact your school admin to be added to the register.",
        );
      } else if (err instanceof ApiError) {
        setError(err);
      } else {
        setError("We couldn't send the code. Please check your connection and try again.");
      }
    } finally {
      setPending(false);
    }
  }

  async function handleEmailSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Enter the email address you use with ISMS.');
      return;
    }
    await requestCode(trimmed, false);
  }

  async function handleCodeSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const trimmedCode = code.trim();
    if (!/^\d{6}$/.test(trimmedCode)) {
      setError('Enter the 6-digit code from the email.');
      return;
    }
    setPending(true);
    setError(null);
    setInfoMessage(null);
    try {
      await client.volunteerSignInVerify({ email: email.trim(), code: trimmedCode });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invalid_code') {
        setError("That code didn't work. It may have expired — request a new one.");
      } else if (err instanceof ApiError) {
        setError(err);
      } else {
        setError("We couldn't verify the code. Please check your connection and try again.");
      }
    } finally {
      setPending(false);
    }
  }

  function handleBack(): void {
    setStep('enter-email');
    setCode('');
    setError(null);
    setInfoMessage(null);
  }

  async function handleResend(): Promise<void> {
    setCode('');
    await requestCode(email.trim(), true);
  }

  return (
    <main className="signin-page" aria-labelledby="signin-heading">
      <div className="signin-card">
        <h1 id="signin-heading">Sign in to ISMS Volunteer Compliance</h1>
        <p className="signin-intro">
          Access your compliance dashboard with a one-time code sent to your email.
        </p>

        {step === 'enter-email' ? (
          <form onSubmit={handleEmailSubmit} noValidate>
            <label htmlFor={emailInputId}>Email address</label>
            <input
              id={emailInputId}
              ref={emailInputRef}
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={pending}
              aria-describedby={error ? errorId : undefined}
              aria-invalid={error ? true : undefined}
            />
            <button type="submit" disabled={pending}>
              {pending ? 'Sending…' : 'Send me a code'}
            </button>
            <p className="signin-admin-hint">
              <Link to="/admin">I'm an admin</Link>
            </p>
          </form>
        ) : (
          <form onSubmit={handleCodeSubmit} noValidate>
            <p className="signin-email-echo">
              Signing in as <strong>{email}</strong>.
            </p>
            <label htmlFor={codeInputId}>6-digit code</label>
            <input
              id={codeInputId}
              ref={codeInputRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              required
              disabled={pending}
              aria-describedby={error ? errorId : infoMessage ? infoId : undefined}
              aria-invalid={error ? true : undefined}
            />
            <div className="signin-actions">
              <button type="submit" disabled={pending}>
                {pending ? 'Verifying…' : 'Verify and sign in'}
              </button>
              <button
                type="button"
                onClick={handleBack}
                disabled={pending}
                className="signin-link-button"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => {
                  void handleResend();
                }}
                disabled={pending}
                className="signin-link-button"
              >
                Resend code
              </button>
            </div>
          </form>
        )}

        {error ? (
          <div id={errorId} className="signin-error">
            <ErrorBanner error={error} />
          </div>
        ) : null}
        {infoMessage && !error ? (
          <p id={infoId} className="signin-info">
            {infoMessage}
          </p>
        ) : null}
      </div>
    </main>
  );
}
